import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Payment section embedded in the Admin Appointment edit form.
 *
 * Behaviors:
 *  - Fetches summary { next_due, payment_mode, saved_method }
 *  - If saved card exists → "Charge $X to Visa •••• 4242" 1-click button
 *  - Always: "Pay with Stripe" → Stripe-hosted Checkout (card is saved for next time)
 *  - "View Transactions" → list + per-tx refund (admins/owners only)
 */
export default function StripePaymentSection({ appointment, calculatedTotal, onChanged, isAdmin = true }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loading, setLoading] = useState(false);
  const [chargingOnFile, setChargingOnFile] = useState(false);
  const [showRefunds, setShowRefunds] = useState(false);
  const [txs, setTxs] = useState([]);
  const [refundForm, setRefundForm] = useState({});

  const authHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSummary = useCallback(async () => {
    if (!appointment?.id) return;
    setLoadingSummary(true);
    try {
      const { data } = await axios.get(
        `${API}/api/payments/appointment/${appointment.id}/summary`,
        { headers: authHeader() }
      );
      setSummary(data);
    } catch (e) {
      // Tolerant — likely "Stripe not configured" if not set up yet
      setSummary({
        total_price: appointment.total_price || calculatedTotal || 0,
        paid_amount: appointment.payment_amount || 0,
        balance: (appointment.total_price || calculatedTotal || 0) - (appointment.payment_amount || 0),
        next_due: 0,
        payment_mode: "none",
        payment_status: appointment.payment_status || "unpaid",
        saved_method: null,
        _error: e?.response?.data?.detail || null,
      });
    } finally {
      setLoadingSummary(false);
    }
  }, [appointment?.id, appointment?.total_price, appointment?.payment_amount, appointment?.payment_status, calculatedTotal]);

  const fetchTransactions = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/payments/transactions?kind=appointment&related_id=${appointment.id}`,
        { headers: authHeader() }
      );
      setTxs(data.transactions || []);
    } catch (e) {
      console.error("Failed to fetch transactions", e);
    }
  };

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (showRefunds) fetchTransactions(); }, [showRefunds]); // eslint-disable-line

  const handlePayNow = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/payments/checkout/appointment`,
        { appointment_id: appointment.id, origin_url: window.location.origin, amount_mode: "auto" },
        { headers: authHeader() }
      );
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const handleChargeOnFile = async () => {
    if (!summary?.saved_method) return;
    const card = summary.saved_method;
    if (!window.confirm(`Charge $${summary.next_due.toFixed(2)} to ${card.brand?.toUpperCase()} •••• ${card.last4}?`)) return;
    setChargingOnFile(true);
    try {
      const { data } = await axios.post(
        `${API}/api/payments/charge-on-file`,
        { appointment_id: appointment.id },
        { headers: authHeader() }
      );
      alert(`Charged $${data.amount.toFixed(2)} to card on file ✓`);
      await fetchSummary();
      if (onChanged) onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Charge failed";
      if (window.confirm(`${detail}\n\nWould you like to try paying via Stripe Checkout instead?`)) {
        handlePayNow();
      }
    } finally {
      setChargingOnFile(false);
    }
  };

  const handleRefund = async (tx) => {
    const form = refundForm[tx.id] || {};
    const refundable = (tx.amount - (tx.refunded_amount || 0)).toFixed(2);
    if (!window.confirm(`Refund $${form.amount || refundable} on this transaction?`)) return;
    try {
      const { data } = await axios.post(
        `${API}/api/payments/refund`,
        { transaction_id: tx.id, amount: form.amount ? parseFloat(form.amount) : null, reason: form.reason || null },
        { headers: authHeader() }
      );
      alert(`Refunded $${data.amount.toFixed(2)}`);
      await fetchTransactions();
      await fetchSummary();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Refund failed");
    }
  };

  if (loadingSummary) {
    return <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-400">Loading payment info…</div>;
  }

  const total = summary?.total_price || appointment.total_price || calculatedTotal || 0;
  const nextDue = summary?.next_due ?? Math.max(total - (summary?.paid_amount || 0), 0);
  const fullyPaid = summary?.payment_status === "paid" || nextDue <= 0;
  const savedMethod = summary?.saved_method;
  const needsSave = !appointment.total_price && calculatedTotal > 0;
  const canPay = !fullyPaid && nextDue > 0;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200" data-testid="stripe-payment-section">
      {/* Mode indicator */}
      {summary && (
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className="text-gray-500">Mode:</span>
          <span className="font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700 capitalize">
            {summary.payment_mode === "partial" ? "Deposit" : summary.payment_mode === "full" ? "Full Payment" : "Off"}
          </span>
          {summary.payment_mode === "partial" && summary.paid_amount > 0 && (
            <span className="text-gray-400">• Deposit paid, balance remaining: ${nextDue.toFixed(2)}</span>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {savedMethod && canPay && (
          <button
            type="button"
            onClick={handleChargeOnFile}
            disabled={chargingOnFile || needsSave}
            data-testid="stripe-charge-on-file-btn"
            className="flex-1 px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 transition-colors flex items-center justify-center gap-1.5"
          >
            {chargingOnFile ? "Charging…" : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Charge ${nextDue.toFixed(2)} to {savedMethod.brand?.toUpperCase()} ••••{savedMethod.last4}
              </>
            )}
          </button>
        )}
        <button
          type="button"
          disabled={!canPay || loading || needsSave}
          onClick={handlePayNow}
          data-testid="stripe-pay-btn"
          className={`${savedMethod && canPay ? "" : "flex-1"} px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5`}
        >
          {loading ? "Redirecting…" : needsSave ? "Save first" : fullyPaid ? "Paid ✓" : savedMethod ? "Use New Card" : `Pay $${nextDue.toFixed(2)}`}
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowRefunds(v => !v)}
            data-testid="stripe-refunds-toggle"
            className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200"
          >
            {showRefunds ? "Hide" : "Transactions"}
          </button>
        )}
      </div>

      {summary?._error && (
        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {summary._error}
        </p>
      )}

      {showRefunds && (
        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="stripe-tx-list">
          {txs.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-2">No transactions yet.</p>
          ) : (
            <div className="space-y-2">
              {txs.map(tx => {
                const refundable = (tx.amount - (tx.refunded_amount || 0)).toFixed(2);
                const canRefund = ["paid", "partially_refunded"].includes(tx.status) && parseFloat(refundable) > 0;
                return (
                  <div key={tx.id} className="bg-white rounded p-2 border border-gray-200 text-xs">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-semibold">${tx.amount.toFixed(2)}</span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
                          tx.status === "paid" ? "bg-green-100 text-green-700" :
                          tx.status === "refunded" ? "bg-red-100 text-red-700" :
                          tx.status === "partially_refunded" ? "bg-orange-100 text-orange-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>{tx.status.replace("_", " ")}</span>
                        {tx.metadata?.charge_type === "off_session" && (
                          <span className="ml-1 text-[10px] text-emerald-700 bg-emerald-50 px-1 rounded">on file</span>
                        )}
                        {tx.refunded_amount > 0 && (
                          <span className="ml-2 text-gray-500">refunded ${tx.refunded_amount.toFixed(2)}</span>
                        )}
                      </div>
                      <span className="text-gray-400">{new Date(tx.created_at).toLocaleDateString()}</span>
                    </div>
                    {canRefund && (
                      <div className="mt-2 flex gap-1.5 items-center">
                        <input
                          type="number" step="0.01" placeholder={`Up to $${refundable}`}
                          value={refundForm[tx.id]?.amount || ""}
                          onChange={(e) => setRefundForm({ ...refundForm, [tx.id]: { ...(refundForm[tx.id] || {}), amount: e.target.value } })}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                          data-testid={`refund-amount-${tx.id}`}
                        />
                        <button type="button" onClick={() => handleRefund(tx)}
                          className="px-2.5 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                          data-testid={`refund-btn-${tx.id}`}>Refund</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
